<template>
  <div :class="['weekdays', classes]">
    <v-btn-toggle
      class="weekdays__days"
      :value="lazyValue"
      :color="validationState"
      multiple
      @change="internalValue = $event"
    >
      <template v-for="(day, index) in weekdays">
        <v-btn
          v-if="weekdaysNames[day]"
          :key="`${index}${day}`"
          :value="day"
          class="weekdays__day"
          :disabled="isDisabled || isReadonly"
          v-bind="$attrs"
        >
          {{ weekdaysNames[day] }}
        </v-btn>
      </template>
    </v-btn-toggle>
    <v-messages
      class="weekdays__messages"
      :color="validationState"
      :value="messagesToDisplay"
    ></v-messages>
  </div>
</template>

<script>
import themeable from 'vuetify/lib/mixins/themeable';
import localable from 'vuetify/lib/mixins/localable';
import validatable from 'vuetify/lib/mixins/validatable';

const weekdaysTypes = ['narrow', 'short', 'long'];

export default {
  name: 'Weekdays',

  mixins: [themeable, localable, validatable],

  props: {
    value: {
      type: Array,
      required: true
    },
    color: {
      type: String,
      default: ''
    },
    weekdays: {
      type: Array,
      default: () => [1, 2, 3, 4, 5, 6, 0],
      validator: prop =>
        Array.isArray(prop) &&
        prop.length === 7 &&
        prop.every(i => i >= 0 && i < 7)
    },
    weekday: {
      type: String,
      default: 'narrow',
      validator: prop => weekdaysTypes.includes(prop)
    }
  },

  data() {
    return {
      hasColor: true
    };
  },

  computed: {
    classes() {
      return {
        weekdays_long: this.calcWeekday === 'long',
        weekdays_short: this.calcWeekday === 'short',
        weekdays_narrow: this.calcWeekday === 'narrow',
        weekdays_readonly: this.readonly,
        weekdays_disabled: this.disabled,
        weekdays_error: this.hasError,
        ...this.themeClasses
      };
    },
    calcWeekday() {
      return weekdaysTypes.includes(this.weekday)
        ? this.weekday
        : weekdaysTypes[0];
    },
    weekdaysNames() {
      const weekdays = [];
      const weekday = this.calcWeekday;

      for (let day = 4; day < 11; day++) {
        weekdays.push(
          new Date(1970, 0, day).toLocaleString(this.currentLocale, { weekday })
        );
      }

      return weekdays;
    },

    messagesToDisplay() {
      if (!this.hasMessages) return [];
      return this.validations
        .map(validation => {
          if (typeof validation === 'string') return validation;
          const validationResult = validation(this.internalValue);
          return typeof validationResult === 'string' ? validationResult : '';
        })
        .filter(message => message !== '');
    }
  }
};
</script>

<style lang="scss" scoped>
@import '~vuetify/src/styles/styles.sass';

@include theme(weekdays) using ($material) {
  &_readonly .weekdays__day {
    color: map-deep-get($material, 'text', 'primary') !important;
    border-color: map-get($material, 'dividers') !important;
    background-color: map-get($material, 'app-bar') !important;
  }

  &_readonly .weekdays__day.v-btn--active {
    color: inherit !important;
  }

  &_readonly .v-btn:not(.v-btn--flat):not(.v-btn--text):not(.v-btn--outlined) {
    background-color: map-get($material, 'app-bar') !important;
  }

  & .theme--light.v-btn-toggle:not(.v-btn-toggle--group) {
    background-color: transparent;
  }
}

.weekdays {
  &__day {
    position: relative;
    margin-right: 4px;
    border-width: thin !important;
    border-radius: 50% !important;

    &:last-child {
      margin-right: 0;
    }
  }

  &_narrow &__day {
    width: 30px !important;
    height: 30px !important;
    min-width: 30px !important;
  }

  &_short &__day {
    width: 48px !important;
    height: 48px !important;
    min-width: 48px !important;
  }

  &_long &__day {
    border-radius: inherit !important;
  }

  &_error &__day {
    color: inherit !important;
  }

  &__days {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    width: 100%;
    margin-bottom: 8px;
  }

  &__messages {
    width: 100%;
  }
}
</style>
